import type { ChapterModule } from "@/data/chapters/types";

export type SearchHitKind =
  | "chapter"
  | "glossary"
  | "objective"
  | "concept"
  | "procedure"
  | "example"
  | "comparison"
  | "misconception";

export interface SearchHit {
  kind: SearchHitKind;
  chapterId: string;
  chapterNumber: number;
  chapterTitle: string;
  label: string;
  snippet?: string;
  anchor?: string;
  haystack: string;
  score?: number;
}

const KIND_BOOST: Record<SearchHitKind, number> = {
  chapter: 30,
  glossary: 20,
  concept: 12,
  objective: 8,
  procedure: 8,
  comparison: 6,
  example: 4,
  misconception: 4,
};

export function buildIndex(chapters: ChapterModule[]): SearchHit[] {
  const out: SearchHit[] = [];
  for (const c of chapters) {
    const head = {
      chapterId: c.meta.id,
      chapterNumber: c.meta.number,
      chapterTitle: c.meta.title,
    };
    out.push({
      ...head,
      kind: "chapter",
      label: `${c.meta.number}. ${c.meta.title}`,
      snippet: c.meta.subtitle,
      haystack: `${c.meta.title} ${c.meta.subtitle} ${c.meta.id} ${c.meta.number} ${c.meta.part}`.toLowerCase(),
    });
    for (const g of c.glossary) {
      out.push({
        ...head,
        kind: "glossary",
        label: g.term,
        snippet: g.definition,
        anchor: "glossary",
        haystack: `${g.term} ${g.definition}`.toLowerCase(),
      });
    }
    for (const o of c.objectives) {
      out.push({
        ...head,
        kind: "objective",
        label: o.text,
        anchor: "objectives",
        haystack: o.text.toLowerCase(),
      });
    }
    for (const k of c.coreConcepts) {
      out.push({
        ...head,
        kind: "concept",
        label: k.title,
        snippet: k.body[0],
        anchor: "concepts",
        haystack: `${k.title} ${k.body.join(" ")}`.toLowerCase(),
      });
    }
    for (const p of c.procedures) {
      out.push({
        ...head,
        kind: "procedure",
        label: p.title,
        snippet: p.intro,
        anchor: "procedures",
        haystack: `${p.title} ${p.intro ?? ""} ${p.steps.map(s => `${s.title} ${s.detail}`).join(" ")}`.toLowerCase(),
      });
    }
    for (const ex of c.examples) {
      out.push({
        ...head,
        kind: "example",
        label: ex.scenario.length > 90 ? `${ex.scenario.slice(0, 87)}...` : ex.scenario,
        snippet: ex.application,
        anchor: "examples",
        haystack: `${ex.scenario} ${ex.application}`.toLowerCase(),
      });
    }
    for (const cmp of c.comparisons) {
      out.push({
        ...head,
        kind: "comparison",
        label: cmp.title,
        snippet: `${cmp.left.label} vs ${cmp.right.label}`,
        anchor: "comparisons",
        haystack: `${cmp.title} ${cmp.left.label} ${cmp.right.label} ${cmp.left.points.join(" ")} ${cmp.right.points.join(" ")}`.toLowerCase(),
      });
    }
    for (const m of c.misconceptions) {
      out.push({
        ...head,
        kind: "misconception",
        label: m.myth.length > 90 ? `${m.myth.slice(0, 87)}...` : m.myth,
        snippet: m.reality,
        anchor: "misconceptions",
        haystack: `${m.myth} ${m.reality}`.toLowerCase(),
      });
    }
  }
  return out;
}

export function searchIndex(index: SearchHit[], rawQuery: string, limit = 60): SearchHit[] {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  const scored: { hit: SearchHit; score: number }[] = [];
  for (const hit of index) {
    let score = 0;
    let allMatch = true;
    const labelLower = hit.label.toLowerCase();
    for (const t of tokens) {
      if (!hit.haystack.includes(t)) {
        allMatch = false;
        break;
      }
      if (labelLower === t) score += 100;
      else if (labelLower.startsWith(t)) score += 45;
      else if (labelLower.includes(t)) score += 22;
      else score += 5;
    }
    if (!allMatch) continue;
    score += KIND_BOOST[hit.kind];
    scored.push({ hit, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(x => ({ ...x.hit, score: x.score }));
}

export interface HighlightPart {
  text: string;
  match: boolean;
}

export function highlight(text: string, query: string): HighlightPart[] {
  const q = query.trim().toLowerCase();
  if (!q || !text) return [{ text, match: false }];
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const ranges: [number, number][] = [];
  for (const t of tokens) {
    let i = 0;
    while ((i = lower.indexOf(t, i)) !== -1) {
      ranges.push([i, i + t.length]);
      i += t.length;
    }
  }
  if (ranges.length === 0) return [{ text, match: false }];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [a, b] of ranges) {
    const last = merged[merged.length - 1];
    if (last && last[1] >= a) {
      last[1] = Math.max(last[1], b);
    } else {
      merged.push([a, b]);
    }
  }
  const result: HighlightPart[] = [];
  let cursor = 0;
  for (const [a, b] of merged) {
    if (a > cursor) result.push({ text: text.slice(cursor, a), match: false });
    result.push({ text: text.slice(a, b), match: true });
    cursor = b;
  }
  if (cursor < text.length) result.push({ text: text.slice(cursor), match: false });
  return result;
}
